import SectionMessage from '@atlaskit/section-message';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { PartialOutageError } from '@atlassian/mpac-primitives';
import { AtlassianProductHostingType, MarketplaceAppPaymentModel, MarketplaceBillingCycle, MarketplacePartnerType } from '@atlassian/mpac-types/graphql-gateway-types';
import { MarketplaceAppUtils } from '@atlassian/mpac-utils';
import React from 'react';
import { FAQWrapper, TabContentContainer, TabEditionsContainer } from '../../../AppListing.styles';
import { useAppPricingPlan } from '../hooks';
import PaidViaPartnerPricing from './PaidViaPartnerPricing/PaidViaPartnerPricing';
import { PlansPricingContainer } from './PlansPricing';
import PricingCalculatorSection from './PricingCalculatorSection';
import PricingCalculatorSkeleton from './PricingCalculatorSection/PricingCalculatorSkeleton';
import PricingFAQs from './PricingFAQs';
import PricingHeading from './PricingHeading';
import { BILLING_CYCLE_FOR_HOSTING } from './PricingTab.constants';
import { PricingCalculatorErrorContainer, PricingDescription as Description, Section, ServerEolMessageContainer } from './PricingTab.styles';
import PricingTiers from './PricingTiers/PricingTiers';
import PricingTiersSkeleton from './PricingTiers/PricingTiersSkeleton';
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
const HeadingDescription = _ref => {
  let {
    showServerMessage,
    isDCHost
  } = _ref;
  return !showServerMessage ? /*#__PURE__*/_jsxs(Description, {
    children: [isDCHost && 'Data Center apps are sold as an annual subscription. ', "All apps offer a free trial for 30 days for teams of all sizes."]
  }) : null;
};
export const PaidViaAtlassianPricing = _ref2 => {
  let {
    appId,
    hosting,
    mpacBaseUrl,
    onPricingCalculatorInstallCTAClick,
    compatibleProduct,
    showServerEolMessage
  } = _ref2;
  const {
    annualPlan,
    monthlyPlan,
    loading
  } = useAppPricingPlan({
    appId,
    hosting,
    mpacBaseUrl
  });
  const isCloud = hosting === AtlassianProductHostingType.Cloud;
  if (isCloud) {
    if (monthlyPlan && annualPlan) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(HeadingDescription, {
          showServerMessage: showServerEolMessage
        }), /*#__PURE__*/_jsx(PricingCalculatorSection, {
          annualPlan: annualPlan,
          monthlyPlan: monthlyPlan,
          onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick,
          compatibleProduct: compatibleProduct
        })]
      });
    } else if (loading) return /*#__PURE__*/_jsx(PricingCalculatorSkeleton, {
      showServerEolMessage: showServerEolMessage
    });
  } else {
    const applicableBillingCycle = BILLING_CYCLE_FOR_HOSTING[hosting];
    const pricingPlan = applicableBillingCycle === MarketplaceBillingCycle.Monthly ? monthlyPlan : annualPlan;
    if (pricingPlan && applicableBillingCycle) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(HeadingDescription, {
          showServerMessage: showServerEolMessage,
          isDCHost: true
        }), /*#__PURE__*/_jsx(PricingTiers, {
          hosting: hosting,
          pricingPlan: pricingPlan,
          applicableBillingCycle: applicableBillingCycle
        })]
      });
    } else if (loading) return /*#__PURE__*/_jsx(PricingTiersSkeleton, {
      showServerEolMessage: showServerEolMessage
    });
  }
  const component = isCloud ? 'calculator' : 'tiers';
  return /*#__PURE__*/_jsx(PricingCalculatorErrorContainer, {
    isCalculator: isCloud,
    children: /*#__PURE__*/_jsx(PartialOutageError, {
      heading: "We\u2019re unable to load the pricing ".concat(component)
    })
  });
};
const PricingTab = _ref3 => {
  let {
    appId,
    appKey,
    compatibleProducts,
    hasDataCenterVersion,
    hosting,
    learnMoreUrl,
    marketingLabels,
    partnerType,
    paymentModel,
    mpacBaseUrl,
    onPricingCalculatorInstallCTAClick,
    showServerEolMessage,
    serverEolStartDate,
    isEditionsSupported
  } = _ref3;
  const isPaidViaPartner = paymentModel === MarketplaceAppPaymentModel.PaidViaPartner;
  const {
    enableSelfServeAppEditions
  } = useStatsig();
  const shouldShowPlans = enableSelfServeAppEditions() && isEditionsSupported;
  const getFAQs = _ref4 => {
    let {
      showPlansFAQs
    } = _ref4;
    return /*#__PURE__*/_jsx(PricingFAQs, {
      paymentModel: paymentModel,
      hosting: hosting,
      marketingLabels: marketingLabels,
      compatibleProducts: compatibleProducts,
      serverSubscriptionApp: MarketplaceAppUtils.isServerSubscriptionApp(appKey),
      hasDataCenterVersion: hasDataCenterVersion,
      byAtlassian: partnerType === MarketplacePartnerType.AtlassianInternal,
      showServerEolFAQs: showServerEolMessage,
      serverEolStartDate: serverEolStartDate,
      showPlansFAQs: showPlansFAQs
    });
  };
  if (shouldShowPlans && !isPaidViaPartner) {
    return /*#__PURE__*/_jsxs(TabEditionsContainer, {
      children: [/*#__PURE__*/_jsx(Section, {
        children: /*#__PURE__*/_jsx(PlansPricingContainer, {
          appId: appId,
          onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick
        })
      }), /*#__PURE__*/_jsx(FAQWrapper, {
        children: getFAQs({
          showPlansFAQs: true
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(TabContentContainer, {
    children: [/*#__PURE__*/_jsxs(Section, {
      children: [/*#__PURE__*/_jsx(PricingHeading, {
        hosting: hosting
      }), showServerEolMessage && /*#__PURE__*/_jsx(ServerEolMessageContainer, {
        "data-testid": "pricing-tab-server-eol-warning",
        children: /*#__PURE__*/_jsx(SectionMessage, {
          appearance: "information",
          children: /*#__PURE__*/_jsxs("p", {
            children: ["As of ", serverEolStartDate, ", server app renewals will automatically be prorated until the end of support for server on Feb 15, 2024."]
          })
        })
      }), isPaidViaPartner ? /*#__PURE__*/_jsx(PaidViaPartnerPricing, {
        learnMoreUrl: learnMoreUrl
      }) : /*#__PURE__*/_jsx(PaidViaAtlassianPricing, {
        appId: appId,
        hosting: hosting,
        mpacBaseUrl: mpacBaseUrl,
        onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick,
        compatibleProduct: compatibleProducts[0],
        showServerEolMessage: showServerEolMessage
      })]
    }), getFAQs({
      showPlansFAQs: false
    })]
  });
};
export default PricingTab;